/**
 * Detection / Rules - API abuse from API gateway access logs (bank rows
 * II-12, III-58).
 *
 * Limits per session and window (requests, response bytes, distinct ids
 * under one route template), what a tripped limit does, the sessions with a
 * block recorded, and the allowlist for shared service accounts.
 *
 * The text says exactly what happens: detection reads access logs after the
 * requests were answered, and a block is enforced only by a gateway that asks
 * the session check. FOCTTA is not in the request path.
 *
 * Live data: GET /v1/detection/api-abuse/rules, /blocks, /allowlist.
 * Editing rules and the allowlist needs detection:rule:approve; releasing a
 * block needs detection:playbook:run.
 */

import { useCallback, useEffect, useState } from 'react';
import { Gauge, Loader2, Pencil, Plus, ShieldOff, Trash2, UserCheck, X } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCan } from '@/lib/access';
import {
  allowlistApiSessionOfBlock,
  createApiAbuseRule,
  deleteApiAbuseRule,
  listApiAbuseAllowlist,
  listApiAbuseRules,
  listApiSessionBlocks,
  releaseApiSessionBlock,
  removeApiAbuseAllowlistEntry,
  updateApiAbuseRule,
  type ApiAbuseAllowlistEntry,
  type ApiAbuseDeliveryStatus,
  type ApiAbuseRule,
  type ApiAbuseRuleInput,
  type ApiSessionBlock,
} from '@/lib/api';

const DETECTOR_LABEL: Record<ApiSessionBlock['detector'], string> = {
  request_volume: 'Request volume',
  bulk_bytes: 'Bulk extraction (bytes)',
  enumeration: 'Enumeration (many distinct ids)',
};

const EMPTY: ApiAbuseRuleInput = {
  route: '',
  enabled: true,
  windowSeconds: 300,
  maxRequests: 1000,
  maxResponseBytes: null,
  enumerationDistinctIds: 50,
  action: 'alert',
  blockSeconds: 900,
  blockIpSessions: false,
};

const inputCls =
  'glass-lite block w-full px-3 py-1.5 mt-1 rounded-md text-xs border border-border/40 focus:outline-none focus:border-primary';
const labelCls = 'text-[10px] uppercase tracking-wider text-muted-foreground';

function limit(v: number | null, unit: string): string {
  return v === null ? 'off' : `${v.toLocaleString()} ${unit}`;
}

function windowText(seconds: number): string {
  return seconds % 60 === 0 ? `${seconds / 60} min` : `${seconds} s`;
}

/** No line counted for this long reads as a forwarder that may have stopped. */
const STALE_INGEST_MINUTES = 60;
/** A gateway that has not asked the session check for this long may have stopped asking. */
const STALE_CHECK_MINUTES = 10;

function minutesAgo(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 60_000));
}

/** A blank number field means "this check is off". */
function numOrNull(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function ApiAbusePanel() {
  const canEdit = useCan('detection:rule:approve');
  const canRelease = useCan('detection:playbook:run');
  const [rules, setRules] = useState<ApiAbuseRule[]>([]);
  const [blocks, setBlocks] = useState<ApiSessionBlock[]>([]);
  const [status, setStatus] = useState<ApiAbuseDeliveryStatus | null>(null);
  const [allowlist, setAllowlist] = useState<ApiAbuseAllowlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string | null; draft: ApiAbuseRuleInput } | null>(null);
  const [allowing, setAllowing] = useState<{ blockId: string; label: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [r, b, a] = await Promise.all([listApiAbuseRules(), listApiSessionBlocks(), listApiAbuseAllowlist()]);
      setRules(r.rules);
      setBlocks(b.blocks);
      setStatus(b.status ?? null);
      setAllowlist(a.entries);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load API abuse rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function run(key: string, fn: () => Promise<unknown>, fail: string) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await refresh();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : fail);
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!editing) return;
    const ok = await run(
      'save',
      () => {
        if (editing.id) {
          const { route: _route, ...patch } = editing.draft;
          return updateApiAbuseRule(editing.id, patch);
        }
        return createApiAbuseRule(editing.draft);
      },
      'save failed',
    );
    if (ok) setEditing(null);
  }

  async function allow() {
    if (!allowing) return;
    const ok = await run('allow', () => allowlistApiSessionOfBlock(allowing.blockId, allowing.label.trim()), 'allowlist failed');
    if (ok) setAllowing(null);
  }

  const activeBlocks = blocks.filter((b) => b.active).length;

  return (
    <GlassCard className="p-5 mt-6" data-testid="api-abuse-panel">
      <CardHeader className="p-0 mb-3 flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Gauge className="h-4 w-4" /> API abuse from gateway access logs
          </CardTitle>
          <CardDescription className="text-xs mt-1" data-testid="api-abuse-how">
            Read from your API gateway's access logs (AWS API Gateway JSON or NGINX combined), sent by your log forwarder to
            POST /v1/detection/api-abuse/access-logs. Found after the fact: a log line is written once the request has been
            answered. The "*" rule counts all routes of one session together; a route rule counts that route per session.
            Alert records a finding on Decisions. Block also records a block on the session: your gateway refuses that
            session only if it asks the FOCTTA session check (POST /v1/detection/api-session-check) before serving a
            request, for example from a Lambda authorizer or an NGINX auth_request. FOCTTA is not in the request path.
            Re-sent logs are safe: a line already counted (the same API Gateway requestId, or the same line as the same
            copy within its batch) is not counted again, and a forwarder that sends an Idempotency-Key header gets the
            first answer back when it retries. Identical lines within one batch all count, so a flood of one URL is seen.
            The replay memory holds 48 hours or the newest 500,000 lines of the tenant, whichever is shorter: about 55
            minutes at 150 requests a second. Only id-like path values (numbers, UUIDs, hex, e-mails, letter and digit
            codes of 8 or more) count as distinct ids, never static files (paths under assets, static, img, css, js,
            fonts and the like, or ending in .js, .css, .png and the like). A document named by an id (such as
            /v1/statements/100234.pdf or .csv) does count. Dates and page numbers (/page/3) are not counted as ids.
            Short customer codes (such as 7 characters), dates and pages count only when a rule names the route, for
            example "GET /v1/customers/{'{id}'}".
          </CardDescription>
          <p className="text-xs mt-1 text-amber-700 dark:text-amber-400" data-testid="api-abuse-volume-batching">
            Request volume and response bytes on logs without a requestId (NGINX, or AWS lines without one): identical
            lines (same client, second, URL and size) count at most as many times as the most copies any one batch
            held. Batches smaller than one second of logs undercount: 1000 identical lines a second sent in batches of
            500 counted 50%, in batches of 100 counted 10%, in batches of 10 counted 1%. Have your forwarder send
            batches that hold at least a full second of logs. AWS lines with a requestId are unaffected.
          </p>
          <p className="text-xs mt-1 text-amber-700 dark:text-amber-400" data-testid="api-abuse-ip-sessions">
            A source IP address can be many people. When a log line has no principal, API key or user, the session is
            the client address, and behind a load balancer or proxy that does not pass the real client address, NAT, or
            a mobile carrier's CGNAT, one address is many customers. Configure the load balancer to pass the client
            address and the gateway to log it (NGINX real_ip with set_real_ip_from and real_ip_header X-Forwarded-For;
            on AWS, $context.identity.sourceIp is the client address), or better, log a principal. A block rule does not
            block a session that is only an IP address unless you tick "Also block IP sessions" on the rule: such a
            session that passes a limit raises an alert finding that says the address may be shared. A partner's or an
            app's API key, or a client-credentials principal, is many customers too and a block rule blocks it like a
            person: put it on the allowlist or use an alert-only rule for it.
          </p>
          <p className="text-xs mt-1 text-muted-foreground" data-testid="api-abuse-session-check">
            The session check answers 200 (or 403 refused / 204 not refused with ?status=1). It is not limited per IP;
            each gateway credential may ask 60,000 times a minute per FOCTTA controller process by default (set
            FOCTTA_API_SESSION_CHECK_MAX_PER_MINUTE above your peak). It answers 503 with Retry-After when it cannot read
            the blocks within about half a second or the credential passed that allowance, 401 or 403 for a missing or
            wrong credential, 400 for a bad body, and never 429. A working gateway credential is never held back by a
            flood of made-up tokens; those are looked up at most 50 a second per process and answered 401. Every limit
            here is per controller process: N processes allow N times each. After issuing a new gateway credential,
            wait about 15 seconds before wiring it into the gateway (until then it can be answered 401). Without an
            active DDR licence, or with a cancelled one, the check answers 402.
            Give the gateway a timeout of 1 to 2 seconds for the check, and decide what a 503 or a timeout means: failing
            open serves the request (detection goes on from the logs; a block is not enforced while FOCTTA cannot answer),
            failing closed refuses every customer whenever FOCTTA cannot answer. We suggest failing open with an alert
            on the gateway: refuse only on a 200 with refuse true (or 403 with ?status=1) and allow on everything else,
            including a timeout, 401, 402 and any 5xx. A gateway that refuses on anything else refuses every customer when
            the DDR licence lapses or is cancelled (402) or the credential is revoked (401). With ?status=1 a credential
            of another purpose or tenant is also answered 403, so after wiring check that a session that is not blocked
            is answered 204. On NGINX, map the check's failures to allow only inside the internal location that calls
            the check, never on the API's server or location: an error_page 401 there also serves requests that fail
            your own authentication (auth_basic). Never add the check's auth_request where your own authentication is
            itself an auth_request (oauth2-proxy, a token introspection service): NGINX keeps one per location, so it
            replaces yours and serves requests with no token. Never use it under satisfy any. The module README has a
            snippet tested on NGINX 1.27, with
            the codes it lets through, where to log a check that failed open, and its limits (a session holding a quote
            or backslash is never refused).
          </p>
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => setEditing({ id: null, draft: { ...EMPTY } })} data-testid="api-abuse-new">
            <Plus className="h-4 w-4" /> Rule
          </Button>
        )}
      </CardHeader>

      {error && (
        <div className="glass-lite rounded-md p-2 mb-3 text-[11px] text-destructive border border-destructive/40">{error}</div>
      )}
      {loading ? (
        <div className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading API abuse rules...
        </div>
      ) : (
        <>
          {rules.length === 0 ? (
            <div className="text-xs text-muted-foreground mb-4">No rules: access logs are read but nothing is flagged.</div>
          ) : (
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-muted-foreground text-left border-b border-border/40">
                    <th className="py-2 pr-3">Route</th>
                    <th className="py-2 pr-3">Window</th>
                    <th className="py-2 pr-3">Requests / session</th>
                    <th className="py-2 pr-3">Bytes / session</th>
                    <th className="py-2 pr-3">Distinct ids</th>
                    <th className="py-2 pr-3">Action</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r) => (
                    <tr key={r.id} className="border-b border-border/30" data-testid="api-abuse-rule">
                      <td className="py-2 pr-3 font-mono">
                        {r.route === '*' ? '* (all routes of a session)' : r.route}
                        {!r.enabled && <Badge variant="glass" className="ml-2 text-[9px]">off</Badge>}
                      </td>
                      <td className="py-2 pr-3 tabular">{windowText(r.windowSeconds)}</td>
                      <td className="py-2 pr-3 tabular">{limit(r.maxRequests, 'requests')}</td>
                      <td className="py-2 pr-3 tabular">{limit(r.maxResponseBytes, 'bytes')}</td>
                      <td className="py-2 pr-3 tabular">{limit(r.enumerationDistinctIds, 'ids')}</td>
                      <td className="py-2 pr-3">
                        <Badge variant={r.action === 'block' ? 'critical' : 'warning'} className="text-[9px]">
                          {r.action === 'block' ? `record block ${Math.round(r.blockSeconds / 60)} min` : 'alert'}
                        </Badge>
                        {r.action === 'block' && (
                          <span className="ml-1.5 text-[10px] text-muted-foreground" data-testid="api-abuse-rule-ip">
                            {r.blockIpSessions ? 'IP sessions blocked too' : 'IP sessions: alert only'}
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        {canEdit && (
                          <>
                            <Button
                              variant="glass"
                              size="sm"
                              aria-label={`Edit ${r.route}`}
                              onClick={() => {
                                const { id, updatedByActorId: _u, updatedAt: _t, ...draft } = r;
                                setEditing({ id, draft });
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="glass"
                              size="sm"
                              aria-label={`Delete ${r.route}`}
                              disabled={busy === r.id}
                              onClick={() => void run(r.id, () => deleteApiAbuseRule(r.id), 'delete failed')}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {editing && (
            <div className="glass-lite rounded-md p-4 mb-4 border border-border/40" data-testid="api-abuse-editor">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium">{editing.id ? `Edit ${editing.draft.route}` : 'New rule'}</div>
                <Button variant="glass" size="sm" aria-label="Close" onClick={() => setEditing(null)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="api-abuse-route">
                    Route (method and path template, or * for all routes of a session)
                  </label>
                  <input
                    id="api-abuse-route"
                    type="text"
                    disabled={editing.id !== null}
                    value={editing.draft.route}
                    placeholder="GET /v1/accounts/{id}"
                    onChange={(e) => setEditing({ ...editing, draft: { ...editing.draft, route: e.target.value } })}
                    className={`${inputCls} font-mono`}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="api-abuse-action">
                    When a limit is passed
                  </label>
                  <select
                    id="api-abuse-action"
                    value={editing.draft.action}
                    onChange={(e) =>
                      setEditing({ ...editing, draft: { ...editing.draft, action: e.target.value as 'alert' | 'block' } })
                    }
                    className={`${inputCls} bg-transparent`}
                  >
                    <option value="alert">Alert only (record a finding)</option>
                    <option value="block">Record a block (enforced by your gateway's session check)</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="api-abuse-block">
                    Block for (minutes)
                  </label>
                  <input
                    id="api-abuse-block"
                    type="number"
                    min={1}
                    max={1440}
                    value={Math.round(editing.draft.blockSeconds / 60)}
                    onChange={(e) =>
                      setEditing({ ...editing, draft: { ...editing.draft, blockSeconds: Math.round(Number(e.target.value) * 60) } })
                    }
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="api-abuse-window">
                    Window (minutes)
                  </label>
                  <input
                    id="api-abuse-window"
                    type="number"
                    min={1}
                    max={1440}
                    value={Math.round(editing.draft.windowSeconds / 60)}
                    onChange={(e) =>
                      setEditing({ ...editing, draft: { ...editing.draft, windowSeconds: Math.round(Number(e.target.value) * 60) } })
                    }
                    className={inputCls}
                  />
                </div>
                {(
                  [
                    ['maxRequests', 'Max requests per session'],
                    ['maxResponseBytes', 'Max response bytes per session'],
                    ['enumerationDistinctIds', 'Max distinct ids (5 to 1000)'],
                  ] as const
                ).map(([key, label]) => (
                  <div key={key}>
                    <label className={labelCls} htmlFor={`api-abuse-${key}`}>
                      {label} (blank = off)
                    </label>
                    <input
                      id={`api-abuse-${key}`}
                      type="number"
                      min={1}
                      value={editing.draft[key] ?? ''}
                      onChange={(e) => setEditing({ ...editing, draft: { ...editing.draft, [key]: numOrNull(e.target.value) } })}
                      className={inputCls}
                    />
                  </div>
                ))}
                {editing.draft.action === 'block' && (
                  <label className="flex items-start gap-2 text-xs mt-5 sm:col-span-2" data-testid="api-abuse-block-ip">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={editing.draft.blockIpSessions}
                      onChange={(e) => setEditing({ ...editing, draft: { ...editing.draft, blockIpSessions: e.target.checked } })}
                    />
                    <span>
                      Also block IP sessions. Off by default: a session that is only a source IP address may be many people
                      (a load balancer without the real client address, NAT, mobile CGNAT), and a block refuses all of them.
                    </span>
                  </label>
                )}
                <label className="flex items-center gap-2 text-xs mt-5">
                  <input
                    type="checkbox"
                    checked={editing.draft.enabled}
                    onChange={(e) => setEditing({ ...editing, draft: { ...editing.draft, enabled: e.target.checked } })}
                  />
                  Rule on
                </label>
              </div>
              <p className="text-xs mt-2 text-muted-foreground" data-testid="api-abuse-volume-batching-editor">
                Max requests and max response bytes undercount when your forwarder sends less than a full second of logs a
                batch and the lines carry no requestId (batches of 100 at 1000 lines a second counted 10%).
              </p>
              <div className="flex justify-end mt-3">
                <Button size="sm" onClick={() => void save()} disabled={busy === 'save' || editing.draft.route.trim() === ''}>
                  {busy === 'save' ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : null}
                  Save
                </Button>
              </div>
            </div>
          )}

          {status && (
            <div className="glass-lite rounded-md p-2 mb-4 text-[11px] border border-border/40" data-testid="api-abuse-delivery">
              <div data-testid="api-abuse-last-ingest">
                {status.lastLineCountedAt === null ? (
                  <>
                    <Badge variant="warning" className="text-[9px] mr-1.5">no logs</Badge>
                    No access log line counted in the last {status.seenLineHours} hours: check your log forwarder.
                  </>
                ) : minutesAgo(status.lastLineCountedAt) >= STALE_INGEST_MINUTES ? (
                  <>
                    <Badge variant="warning" className="text-[9px] mr-1.5">stale</Badge>
                    Last access log line counted {minutesAgo(status.lastLineCountedAt)} minutes ago (
                    {new Date(status.lastLineCountedAt).toLocaleString()}): check your log forwarder.
                  </>
                ) : (
                  <>Last access log line counted {new Date(status.lastLineCountedAt).toLocaleString()}.</>
                )}
              </div>
              {(status.pathTemplateMismatch ?? 0) > 0 && (
                <div className="mt-1" data-testid="api-abuse-template-mismatch">
                  <Badge variant="warning" className="text-[9px] mr-1.5">format</Badge>
                  {status.pathTemplateMismatch}
                  {status.maxMismatchCounted !== undefined && (status.pathTemplateMismatch ?? 0) >= status.maxMismatchCounted ? ' or more' : ''}{' '}
                  access log line{status.pathTemplateMismatch === 1 ? '' : 's'} in the last {status.seenLineHours} hours had a path
                  that did not match the gateway template (resourcePath or routeKey)
                  {status.lastPathTemplateMismatchAt ? `, last at ${new Date(status.lastPathTemplateMismatchAt).toLocaleString()}` : ''}.
                  They were read as plain paths, so a rule on the template route may not see them. Check that the forwarder logs
                  $context.path and $context.stage as the README asks.
                </div>
              )}
              {status.sessionCheckCallers !== undefined && (
                <div className="mt-1" data-testid="api-abuse-check-callers">
                  {status.sessionCheckCallers.every((c) => c.lastCheckAt === null) ? (
                    <>
                      <Badge variant="warning" className="text-[9px] mr-1.5">not asked</Badge>
                      No gateway has asked the session check: blocks are recorded but nothing refuses those sessions.
                      {status.sessionCheckCallers.length === 0 ? ' Issue a decision_gateway credential for your API gateway.' : ''}
                    </>
                  ) : (
                    <>Session check asked by:</>
                  )}
                  {status.sessionCheckCallers.length > 0 && (
                    <ul className="mt-0.5 space-y-0.5">
                      {status.sessionCheckCallers.map((c) => (
                        <li key={c.credentialId ?? c.label} data-testid="api-abuse-check-caller">
                          <span className="font-mono">{c.label}</span>
                          {c.revoked ? ' (revoked)' : ''}:{' '}
                          {c.lastCheckAt === null ? (
                            'never asked (expected for a credential used only by the SQL gateway)'
                          ) : minutesAgo(c.lastCheckAt) >= STALE_CHECK_MINUTES ? (
                            <>
                              <Badge variant="warning" className="text-[9px] mr-1">stale</Badge>
                              last asked {minutesAgo(c.lastCheckAt)} minutes ago ({new Date(c.lastCheckAt).toLocaleString()}): check
                              that the gateway still asks
                            </>
                          ) : (
                            <>last asked {new Date(c.lastCheckAt).toLocaleString()}</>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {(status.auditPending > 0 || status.notificationsPending > 0) && (
                <div className="mt-1" data-testid="api-abuse-pending">
                  <Badge variant="warning" className="text-[9px] mr-1.5">waiting</Badge>
                  {status.auditPending} audit event{status.auditPending === 1 ? '' : 's'} and {status.notificationsPending}{' '}
                  notification{status.notificationsPending === 1 ? '' : 's'} not written yet
                  {status.oldestPendingAt ? ` (oldest ${new Date(status.oldestPendingAt).toLocaleString()})` : ''}. The
                  findings are recorded; these are written by the next access log ingest. If the number keeps growing, the
                  audit trail may be down: contact support@foctta.com.
                </div>
              )}
            </div>
          )}

          <div className="text-sm font-medium mb-2 flex items-center gap-2">
            <ShieldOff className="h-4 w-4" /> Sessions with a block recorded
            <Badge variant={activeBlocks > 0 ? 'critical' : 'glass'} className="text-[9px]">
              {activeBlocks} in force
            </Badge>
          </div>
          {blocks.length === 0 ? (
            <div className="text-xs text-muted-foreground mb-4">No session has a block recorded.</div>
          ) : (
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-muted-foreground text-left border-b border-border/40">
                    <th className="py-2 pr-3">Decision</th>
                    <th className="py-2 pr-3">Route</th>
                    <th className="py-2 pr-3">Session</th>
                    <th className="py-2 pr-3">Pattern</th>
                    <th className="py-2 pr-3">Block ends</th>
                    <th className="py-2 pr-3">Seen again</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {blocks.map((b) => (
                    <tr key={b.id} className="border-b border-border/30" data-testid="api-abuse-block">
                      <td className="py-2 pr-3 font-mono">
                        <a className="underline" href={`/detection/decisions?reqId=${encodeURIComponent(b.requestId)}`}>
                          {b.requestId}
                        </a>
                      </td>
                      <td className="py-2 pr-3 font-mono">{b.route === '*' ? 'all routes' : b.route}</td>
                      <td className="py-2 pr-3" data-testid="api-abuse-block-kind">
                        {b.sessionKind === 'ip' ? (
                          <Badge variant="warning" className="text-[9px]" title="Everyone behind this address is refused">
                            IP address, may be many people
                          </Badge>
                        ) : (
                          'principal'
                        )}
                      </td>
                      <td className="py-2 pr-3">{DETECTOR_LABEL[b.detector] ?? b.detector}</td>
                      <td className="py-2 pr-3 tabular">{new Date(b.blockedUntil).toLocaleString()}</td>
                      <td className="py-2 pr-3 tabular" title="Windows that passed a limit again while the block was in force">
                        {b.tripsWhileBlocked > 0 ? `${b.tripsWhileBlocked} window${b.tripsWhileBlocked === 1 ? '' : 's'}` : 'no'}
                      </td>
                      <td className="py-2 pr-3">
                        {b.active ? (
                          <Badge variant="critical" className="text-[9px]">in force</Badge>
                        ) : b.releasedAt ? (
                          <Badge variant="glass" className="text-[9px]">released</Badge>
                        ) : (
                          <Badge variant="glass" className="text-[9px]">expired</Badge>
                        )}
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        {b.active && canRelease && (
                          <Button
                            variant="glass"
                            size="sm"
                            disabled={busy === b.id}
                            onClick={() => void run(b.id, () => releaseApiSessionBlock(b.id), 'release failed')}
                          >
                            Release
                          </Button>
                        )}
                        {canEdit && (
                          <Button variant="glass" size="sm" onClick={() => setAllowing({ blockId: b.id, label: '' })}>
                            <UserCheck className="h-3 w-3" /> Allowlist
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {blocks.some((b) => b.active && b.tripsWhileBlocked > 0) && (
            <div className="text-[11px] text-muted-foreground mb-4" data-testid="api-abuse-seen-again">
              A session seen again while its block is in force means its requests are still being served: check that your
              gateway asks the session check.
            </div>
          )}

          {allowing && (
            <div className="glass-lite rounded-md p-3 mb-4 border border-border/40" data-testid="api-abuse-allow-editor">
              <label className={labelCls} htmlFor="api-abuse-allow-label">
                Allowlist this session (a shared service account): its requests are no longer counted and the session check
                never refuses it. Label
              </label>
              <input
                id="api-abuse-allow-label"
                type="text"
                maxLength={120}
                value={allowing.label}
                placeholder="Payments batch service account"
                onChange={(e) => setAllowing({ ...allowing, label: e.target.value })}
                className={inputCls}
              />
              <div className="flex justify-end gap-2 mt-2">
                <Button variant="glass" size="sm" onClick={() => setAllowing(null)}>
                  Cancel
                </Button>
                <Button size="sm" disabled={busy === 'allow' || allowing.label.trim() === ''} onClick={() => void allow()}>
                  Add to allowlist
                </Button>
              </div>
            </div>
          )}

          <div className="text-sm font-medium mb-2 flex items-center gap-2">
            <UserCheck className="h-4 w-4" /> Allowlist
          </div>
          {allowlist.length === 0 ? (
            <div className="text-xs text-muted-foreground">No session is allowlisted.</div>
          ) : (
            <ul className="text-xs space-y-1">
              {allowlist.map((a) => (
                <li key={a.id} className="flex items-center justify-between" data-testid="api-abuse-allow">
                  <span>{a.label}</span>
                  {canEdit && (
                    <Button
                      variant="glass"
                      size="sm"
                      aria-label={`Remove ${a.label}`}
                      disabled={busy === a.id}
                      onClick={() => void run(a.id, () => removeApiAbuseAllowlistEntry(a.id), 'remove failed')}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </GlassCard>
  );
}
